import * as React from 'react';
import { Alert, Button, Spinner, Stack, Table } from 'react-bootstrap';
import {
  BookingComputedStatus,
  GroupBookingComputedStatusOptions,
  InstitutionGroupEventMemberBookingRow,
} from 'cb-model';
import { fetchApi } from 'gossamer-front-end';
import { NavLink } from 'react-router-dom';
import { getBookingMatrixStatus } from './InstitutionGroupBookingMatrixUtils';
import { displayDateTimeToMinute } from 'packages/gossamer-unified';
import { BookingModal } from './BookingModal';
import classes from './InstitutionGroupBookingMatrix.module.css';

interface MatrixEvent {
  id: number;
  name: string;
  visibilityLimit: string;
  startAt: string | null;
}

interface MatrixMember {
  id: number;
  userId: string;
  name: string;
}

interface BookingCellProps {
  row: InstitutionGroupEventMemberBookingRow;
}

export const InstitutionGroupBookingMatrix = ({
  institutionGroupId,
}: {
  institutionGroupId: number;
}): React.JSX.Element => {
  const [rows, setRows] = React.useState<InstitutionGroupEventMemberBookingRow[]>([]);
  const [eventsAsColumns, setEventsAsColumns] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = React.useState<InstitutionGroupEventMemberBookingRow | null>(null);
  const [click, setClick] = React.useState<number>(0);

  React.useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);
    fetchApi('GET', `/main/institution-group-event-member-bookings/${institutionGroupId}`)
      .then((response: InstitutionGroupEventMemberBookingRow[]) => {
        if (active) {
          setRows(response);
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : 'Unable to load group bookings.');
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [institutionGroupId, click]);

  const refresh = () => setClick((prev) => prev + 1);

  const events = React.useMemo(() => {
    const byId = new Map<number, MatrixEvent>();
    rows.forEach((row) => {
      if (!byId.has(row.event_id)) {
        byId.set(row.event_id, {
          id: row.event_id,
          name: row.event_name,
          visibilityLimit: row.visibility_limit,
          startAt: row.start_at,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => {
      const visibilityComparison = a.visibilityLimit.localeCompare(b.visibilityLimit);
      if (visibilityComparison !== 0) return visibilityComparison;
      if (a.startAt === b.startAt) return a.id - b.id;
      if (a.startAt === null) return 1;
      if (b.startAt === null) return -1;
      return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
    });
  }, [rows]);

  const members = React.useMemo(() => {
    const byId = new Map<number, MatrixMember>();
    rows.forEach((row) => {
      if (!byId.has(row.institution_member_id)) {
        byId.set(row.institution_member_id, {
          id: row.institution_member_id,
          userId: row.user_id,
          name: row.user_name,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name) || a.userId.localeCompare(b.userId));
  }, [rows]);

  const rowsByCombination = React.useMemo(
    () => new Map(rows.map((row) => [`${row.event_id}:${row.institution_member_id}`, row])),
    [rows],
  );

  if (isLoading) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading booking matrix...
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (rows.length === 0) {
    return <Alert variant="info">There are no event/member combinations for this group.</Alert>;
  }

  const getRow = (eventId: number, memberId: number) => {
    const row = rowsByCombination.get(`${eventId}:${memberId}`);
    if (!row) {
      throw new Error(`Missing booking matrix row for event ${eventId} and member ${memberId}`);
    }
    return row;
  };

  const BookingCell = ({ row }: BookingCellProps): React.JSX.Element => {
    const status = getBookingMatrixStatus(row);
    const display = GroupBookingComputedStatusOptions.find((option) => option.id === status);
    const bookingDetail = row.booking_created_at
      ? ` Latest booking: ${new Date(row.booking_created_at).toLocaleString()}.`
      : '';
    return (
      <td
        title={`${display.label}.${bookingDetail}`}
        aria-label={`${display.label}.${bookingDetail}`}
        onClick={() => setSelectedBooking(row)}
        className={classes.BookingCell}
        style={{
          backgroundColor: display.bgColor,
          color: display.textColor,
        }}
      >
        {display.cellLabel}
      </td>
    );
  };

  return (
    <>
      <Stack direction="horizontal" gap={3} className="mb-3" style={{ flexWrap: 'wrap' }}>
        <Button size="sm" variant="outline-primary" onClick={() => setEventsAsColumns((value) => !value)}>
          {eventsAsColumns ? 'Show events as rows' : 'Show members as rows'}
        </Button>
      </Stack>

      {eventsAsColumns ? (
        <Table bordered hover responsive size="sm" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th rowSpan={2} className={classes.MemberLabelColumnHeading}>
                Member
              </th>
              {events.map((event) => (
                <th key={event.id} className={classes.EventColumnHeading}>
                  <div>
                    <NavLink to={`/admin/event/display/${event.id}`}>{event.name}</NavLink>
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              {events.map((event) => (
                <th key={event.id} className={classes.EventColumnSubHeading}>
                  {event.visibilityLimit}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id}>
                <th scope="row">
                  <div>
                    <NavLink to={`/admin/institution-member/display/${member.id}`}>{member.name}</NavLink>
                  </div>
                </th>
                {events.map((event) => (
                  <BookingCell key={event.id} row={getRow(event.id, member.id)} />
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Table bordered hover responsive size="sm" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Event</th>
              <th style={{ minWidth: 100 }}>Visibility</th>
              <th style={{ minWidth: 100 }}>Start</th>
              {members.map((member) => (
                <th key={member.id} className={classes.MemberColumnHeading}>
                  <div>
                    <NavLink to={`/admin/institution-member/display/${member.id}`}>{member.name}</NavLink>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <th scope="row">
                  <NavLink to={`/admin/event/display/${event.id}`}>{event.name}</NavLink>
                </th>
                <td style={{ fontSize: 12 }}>{event.visibilityLimit}</td>
                <td style={{ fontSize: 12 }}>{displayDateTimeToMinute(event.startAt)}</td>
                {members.map((member) => (
                  <BookingCell key={member.id} row={getRow(event.id, member.id)} />
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Stack direction="horizontal" gap={2} style={{ flexWrap: 'wrap' }} className="mt-3">
        <Stack direction="vertical" gap={1}>
          <LegendItem status="upcoming" />
          <LegendItem status="no booking" />
          <LegendItem status="added afterwards" />
        </Stack>
        <Stack direction="vertical" gap={1}>
          <LegendItem status="past, not tracked" />
          <LegendItem status="attended, pending feedback" />
          <LegendItem status="attended, complete" />
        </Stack>
        <Stack direction="vertical" gap={1}>
          <LegendItem status="booking cancelled" />
          <LegendItem status="session did not take place" />
          <LegendItem status="event cancelled" />
          <LegendItem status="past, attendance not recorded" />
          <LegendItem status="did not attend" />
          <LegendItem status="missing booking" />
        </Stack>
      </Stack>

      {!!selectedBooking && (
        <BookingModal
          context="none"
          show={true}
          setShow={(val) => {
            if (!val) setSelectedBooking(null);
          }}
          refresh={refresh}
          {...selectedBooking}
        />
      )}
    </>
  );
};

const LegendItem = ({ status }: { status: BookingComputedStatus }): React.JSX.Element => {
  const option = GroupBookingComputedStatusOptions.find((s) => s.id === status);
  if (!option) return <></>;
  return (
    <span
      key={option.id}
      className={classes.LegendItem}
      style={{
        backgroundColor: option.bgColor,
        color: option.textColor,
      }}
    >
      {option.cellLabel} = {option.label}
    </span>
  );
};
